import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { uploadImage } from '@/lib/api/media';

interface Props {
  value: string;
  onChange: (url: string) => void;
  label?: string;
  /** also show a raw URL input as a fallback */
  allowUrl?: boolean;
}

// Upload an image (to Cloudinary via uploadImage) and return its URL.
// Replaces "paste an image URL" with real image selection + preview.
export default function ImageUploadField({ value, onChange, label, allowUrl = true }: Props) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const handleFile = async (file: File) => {
    if (file.size > 10 * 1024 * 1024) { setErr(t('shared.image_too_large')); return; }
    setBusy(true); setErr('');
    try {
      const url = await uploadImage(file);
      onChange(url);
    } catch {
      setErr(t('shared.image_upload_failed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <label className="block text-xs font-label font-semibold text-foreground-700 mb-1.5">{label ?? t('shared.image')}</label>
      <div className="flex flex-col sm:flex-row items-stretch sm:items-start gap-3">
        <div className="w-20 h-20 rounded-lg overflow-hidden bg-background-100 border border-background-200 flex items-center justify-center flex-shrink-0">
          {value ? (
            <img src={value} alt="" className="w-full h-full object-cover" />
          ) : (
            <i className="ri-image-line text-xl text-foreground-300"></i>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={busy}
            className="w-full sm:w-auto px-3 py-2 min-h-[40px] rounded-lg bg-primary-500 text-white text-xs font-label hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-60 inline-flex items-center justify-center gap-1.5"
          >
            {busy ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-upload-2-line"></i>}
            {busy ? t('shared.image_uploading') : t('shared.image_select')}
          </button>
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); }}
          />
          {allowUrl && (
            <input
              type="text"
              value={value}
              onChange={(e) => onChange(e.target.value)}
              placeholder={t('shared.image_url_placeholder')}
              className="mt-2 w-full px-3 py-2 min-h-[40px] rounded-lg border border-background-200 bg-background-50 text-xs text-foreground-900 font-body focus:outline-none focus:border-primary-400 transition-all"
            />
          )}
          {err && <p className="mt-1 text-[11px] text-red-500 font-body break-words">{err}</p>}
        </div>
      </div>
    </div>
  );
}
